import React, { FunctionComponent, useState, useEffect } from "react";
import Image from "next/image";
import clsx from "clsx";
import { BrandService } from "@/src/BrandService";
import Container from "@/src/components/atoms/Container/Container";
import UserIcon from "../../images/icons/solid/user.svg";
import PhoneIcon from "../../images/icons/solid/phone.svg";
import Logo from "@/src/media/logo.svg";
import HeaderMenu from "@/src/components/molecules/HeaderMenu/HeaderMenu";
import Breadcrumbs, { IBreadCrumb } from "@/src/components/molecules/BreadCrumbs";
import { useScrollPosition } from "@/src/hooks";
import { Button } from "@/src/components/v2/Button/Button";
import { SmartLink } from "@/src/components/v2/SmartLink/SmartLink";
import SVG from "react-inlinesvg";
import { PublicSettings } from "@/src/config/PublicSettings";
import { CartWidget } from "../molecules/CartWidget";
import { useCartTitle } from "@/src/hooks/useCartTitle";
import { useRouter } from "next/router";
import siteConfig from "@/src/config/SiteConfig";
import { FocusTrap } from "focus-trap-react";
import { Utils } from "@/src/Utils";
import { resolveCtaLink } from "@/src/utils/resolveCtaLink";

const BRAND_DATA = BrandService.getCurrentBrandData();

export interface SiteHeaderProps {
    displayCTA?: boolean;
    breadcrumbs?: IBreadCrumb[];
    displayLogoOnly?: boolean;
    displayPhone?: boolean;
    employerPhoneNumber?: boolean;
    isBuilderPage?: boolean;
    breakpoint: string;
    customCtaText?: string;
    customCtaLink?: string;
    customPhoneNumber?: string;
}

export const SiteHeader: FunctionComponent<SiteHeaderProps> = ({ employerPhoneNumber = false, ...props }) => {
    const [isHydrated, setIsHydrated] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { breakpoint } = props;
    const { formatPhone } = Utils;
    const isdefaultBrand = BRAND_DATA.id === "default";

    // Phone number may or may not have formatting
    const phoneNumber = props.customPhoneNumber || (employerPhoneNumber ? PublicSettings.CS_EMPLOYER_PHONE_NUMBER : PublicSettings.CS_PHONE_NUMBER);
    // Remove all non-digit characters from the phone number for consistency, and then format it for display:
    const rawPhoneNumber = phoneNumber.replace(/\D+/g, "");
    const phoneNumberToDisplay = formatPhone(rawPhoneNumber);

    const router = useRouter();
    const ctaLink = resolveCtaLink({ customCtaLink: props.customCtaLink, returnFormId: router.query.returnFormId });
    const cartTitle = useCartTitle(props.customCtaText);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    const onToggleMenuEscape = (event: React.KeyboardEvent) => {
        if (isMenuOpen && event.key === "Escape") {
            setIsMenuOpen(false);
        }
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleNavigate = () => {
        setIsMenuOpen(false);
    };

    const scrollPosition = useScrollPosition();

    return (
        <>
            <header
                id="spotSiteHeader"
                className={clsx("active sticky top-0 z-50 border-b bg-background-surface", scrollPosition > 0 ? "border-border-light" : "border-transparent")}
            >
                <FocusTrap
                    active={isMenuOpen}
                    focusTrapOptions={{
                        clickOutsideDeactivates: true,
                        escapeDeactivates: true
                    }}
                >
                    <nav aria-label="Main menu">
                        {/* ///Pre-nav bar */}
                        {isdefaultBrand && (
                            <div data-testid="pre-navbar" className="bg-background-secondary py-1 ">
                                <Container className="flex w-full flex-row items-center justify-between gap-4 text-body2">
                                    <SmartLink href={`tel:${phoneNumber}`}>
                                        <div className="flex items-center gap-1">
                                            <SVG src={PhoneIcon.src} className="h-5 w-5" />
                                            <span className="font-semibold text-content-brand-b hover:underline">{phoneNumberToDisplay}</span>
                                        </div>
                                    </SmartLink>

                                    <div className="flex items-center gap-4">
                                        <SmartLink href={siteConfig.underwritingLink} className="font-medium text-content-secondary hover:underline">
                                            Underwriting
                                        </SmartLink>

                                        <SmartLink href={PublicSettings.MEMBER_CENTER_URL_ROOT} target="_self" className="hidden md:flex">
                                            <div className="flex items-center gap-2">
                                                <SVG src={UserIcon.src} className="h-5 w-5" />
                                                <span className="font-semibold text-content-brand-b hover:underline">Sign in to Member Center</span>
                                            </div>
                                        </SmartLink>
                                    </div>
                                </Container>
                            </div>
                        )}
                        {/* // Main nav bar */}
                        <Container className="relative flex flex-row items-center gap-1 px-4 py-3 lg:px-9 lg:py-4">
                            {!props.displayLogoOnly && (
                                <button
                                    tabIndex={0}
                                    className="z-50 mr-6 md:mr-2 lg:hidden"
                                    onClick={toggleMenu}
                                    onKeyDown={event => onToggleMenuEscape(event)}
                                    aria-expanded={isMenuOpen}
                                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                                >
                                    <span className="btn btn-circle border-accent-secondary bg-accent-secondary hover:bg-background-contrast">
                                        {/* hamburger icon */}
                                        {isMenuOpen ? (
                                            <svg className="swap-on fill-accent-contrast" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512">
                                                <polygon points="400 145.49 366.51 112 256 222.51 145.49 112 112 145.49 222.51 256 112 366.51 145.49 400 256 289.49 366.51 400 400 366.51 289.49 256 400 145.49" />
                                            </svg>
                                        ) : (
                                            <svg className="swap-off fill-accent-contrast" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512">
                                                <path d="M64,384H448V341.33H64Zm0-106.67H448V234.67H64ZM64,128v42.67H448V128Z" />
                                            </svg>
                                        )}
                                    </span>
                                </button>
                            )}
                            {/* ///Spot Logo */}
                            <div
                                className={`left-0 top-0 mt-1 flex h-full items-center lg:relative ${
                                    props.displayLogoOnly ? "w-auto min-w-[116px] justify-start md:w-full" : "w-full justify-center md:justify-start"
                                } lg:w-auto`}
                            >
                                <SmartLink href="/" className="block" data-testid="header-logo-link" tabIndex={0}>
                                    <Image
                                        className="block"
                                        src={Logo}
                                        width={116}
                                        height={53}
                                        alt={BRAND_DATA.name}
                                        style={{
                                            maxWidth: "100%",
                                            height: "auto"
                                        }}
                                    />
                                </SmartLink>
                            </div>
                            {!props.displayLogoOnly && (
                                <div className="flex-1">
                                    <div
                                        className={`absolute left-0 top-full z-50 w-full bg-background-surface drop-shadow-lg lg:relative lg:left-auto lg:top-auto lg:w-auto lg:bg-auto lg:drop-shadow-none ${
                                            isMenuOpen ? "" : "hidden"
                                        } lg:block`}
                                    >
                                        {isdefaultBrand && (
                                            <div className="border-t-2 border-t-background-medium py-4 md:hidden">
                                                <Container>
                                                    <div className="flex flex-col gap-2">
                                                        <Button href="/quote" title="Click For Price" style="Primary" className="font-semibold" />
                                                        <Button
                                                            href={PublicSettings.MEMBER_CENTER_URL_ROOT}
                                                            target="_self"
                                                            title="Sign in to Member Center"
                                                            style="outline-secondary"
                                                            className="font-medium hover:bg-background-medium"
                                                        />
                                                    </div>
                                                </Container>
                                            </div>
                                        )}
                                        <HeaderMenu
                                            key={router.asPath}
                                            onNavigate={handleNavigate}
                                            breakpoint={breakpoint}
                                            isMobileMenuOpen={isMenuOpen}
                                            isdefaultBrand={isdefaultBrand}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className={`flex items-center justify-end gap-2 ${props.displayLogoOnly ? "w-full" : ""}`}>
                                {!props.displayLogoOnly && !isdefaultBrand && (
                                    <SmartLink title="Sign in to the Member Center" target="_self" href={PublicSettings.MEMBER_CENTER_URL_ROOT} tabIndex={0}>
                                        <div className="flex items-center rounded-lg p-0 text-button-md hover:bg-background-medium sm:p-2">
                                            <SVG src={UserIcon.src} />
                                            <span className="hidden font-bold text-accent-secondary-ada min-[1084px]:inline">Sign In</span>
                                        </div>
                                    </SmartLink>
                                )}
                                {!isdefaultBrand && (
                                    <SmartLink title={phoneNumberToDisplay} href={`tel:${phoneNumber}`} target="_self" tabIndex={0}>
                                        <div className="flex items-center rounded-lg p-0 text-button-md hover:bg-background-medium sm:p-2">
                                            <SVG src={PhoneIcon.src} />
                                            <span className="hidden font-bold text-accent-secondary-ada xl:inline">{phoneNumberToDisplay}</span>
                                        </div>
                                    </SmartLink>
                                )}
                                {/* The CTA slot renders from the first paint (SSR included) with reserved
                                    dimensions so the quote button and cart icon don't shift the header/logo
                                    when they activate after hydration (ENG-16926, Semrush QW-6). The
                                    cookie-derived cartTitle is only applied once hydrated because the
                                    server can't know it; pre-hydration the SSR-safe default label reserves
                                    a near-identical width. */}
                                <div className={clsx("flex items-center gap-2", !isHydrated && "invisible")}>
                                    <div className="h-10 w-10">{isHydrated && <CartWidget id="cart-quote" cartTitle={cartTitle} ctaLink={ctaLink} />}</div>
                                    <div className="hidden md:block">
                                        <Button
                                            href={ctaLink}
                                            id="cta-quote"
                                            title={isHydrated ? cartTitle : props.customCtaText || "Click For Price"}
                                            style="Primary"
                                            className="text-button-sm font-semibold xl:text-button-md"
                                        />
                                    </div>
                                </div>
                            </div>
                            {!isdefaultBrand && (
                                <SmartLink
                                    href={siteConfig.underwritingLink}
                                    className="absolute right-5 top-0 rounded-b-lg bg-text-hint px-1 text-xs text-text-primary hover:bg-background-medium  md:right-9"
                                >
                                    Underwriting
                                </SmartLink>
                            )}
                        </Container>
                    </nav>
                </FocusTrap>
            </header>
            {props.breadcrumbs && (
                <div className="bg-accent-secondary-background">
                    <Container>
                        <Breadcrumbs crumbs={props.breadcrumbs} />
                    </Container>
                </div>
            )}
        </>
    );
};
